"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { AlertTriangle, ExternalLink, Search, ShieldCheck } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { createClient } from "@/lib/supabase/client"
import { readDeepLinkParam } from "@/lib/tools/deep-link"

// Source: U.S. Consumer Product Safety Commission (CPSC) recall database,
// mirrored locally from saferproducts.gov. The verdict is binary —
// either there are matching CPSC recalls (replace tone) or there aren't
// (healthy tone). "Watch" is reserved for the unsearched starting state.

interface RecallResult {
  recall_id: number
  recall_number: string
  recall_date: string
  title: string
  description: string
  url: string
  consumer_contact: string | null
  products: any[]
  images: any[]
  hazards: any[]
  remedies: any[]
}

type Tone = "healthy" | "watch" | "replace"

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · No recall found",
  watch: "Verdict · Pending search",
  replace: "Verdict · Recall match",
}

const RELATED = [
  {
    title: "Refrigerator Age Calculator",
    category: "Specialty",
    description: "Decode build date from your serial number.",
    href: "/tools/refrigerator-age-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Repair vs Replace Calculator",
    category: "Specialty",
    description: "Apply the 50% rule and ENERGY STAR cutoff.",
    href: "/tools/refrigerator-repair-vs-replace-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Lifespan Calculator",
    category: "Specialty",
    description: "Estimate years of life remaining.",
    href: "/tools/refrigerator-lifespan-calculator",
    time: "~45 sec",
  },
  {
    title: "Find Your Fridge",
    category: "Specialty",
    description: "Explore product groups based on your answers.",
    href: "/tools/find-your-fridge",
    time: "~2 min",
  },
]

const FAQS = [
  {
    question: "Where does the recall data come from?",
    answer:
      "The U.S. Consumer Product Safety Commission (CPSC) recall database — the federal source of truth for consumer-product recalls — mirrored from saferproducts.gov.",
  },
  {
    question: "What should I search for?",
    answer:
      "Try brand name first (e.g., Samsung, Frigidaire, LG, and more). If you have your model number from the rating plate, search that next. Partial matches work — try a few variations.",
  },
  {
    question: "What if my fridge is in a recall?",
    answer:
      "Don't ignore it. CPSC recalls are issued for safety hazards (fire, shock, ice-maker injury, mold). Click through to the official CPSC page for the remedy — usually a free repair, replacement, or refund from the manufacturer.",
  },
  {
    question: "What if my fridge isn't in the database?",
    answer:
      "Most refrigerators have never been recalled. A clean search means no matching recalls were found in our mirror of the CPSC database. For ongoing alerts, also check cpsc.gov/Recalls directly and saferproducts.gov.",
  },
  {
    question: "How current is the data?",
    answer:
      "We mirror the CPSC database; for the absolute latest recalls — especially those issued in the past 24–48 hours — confirm at cpsc.gov/Recalls before acting.",
  },
]

export function RefrigeratorRecallCheckerPageClient() {
  const [searchTerm, setSearchTerm] = useState("")
  const [results, setResults] = useState<RecallResult[]>([])
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)

  // The recall-checker widget deep-links here as ?q=<brand or model>. Prefill
  // the box and run the search once, so the visitor never retypes what they
  // already typed into the widget. Mount-only by design.
  const handleSearch = async (overrideTerm?: string) => {
    const raw = (overrideTerm ?? searchTerm).trim()
    if (!raw) return
    setLoading(true)
    try {
      const supabase = createClient()
      const term = raw.toLowerCase()
      const { data, error } = await supabase
        .from("cpsc_recalls")
        .select("*")
        .or(`title.ilike.%${term}%,description.ilike.%${term}%`)
        .order("recall_date", { ascending: false })
      if (!error && data) setResults(data as RecallResult[])
      else setResults([])
    } catch {
      setResults([])
    } finally {
      setLoading(false)
      setSearched(true)
    }
  }

  // Deep-link search runs inside an async block owned by the effect. The rule
  // accepts setState there and rejects it in the effect body proper; the ?q=
  // behaviour is identical.
  useEffect(() => {
    const term = readDeepLinkParam(window.location.search, "q")
    if (!term) return
    void (async () => {
      setSearchTerm(term)
      await handleSearch(term)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * `overrideTerm` exists so the ?q= deep link can search on mount, before
   * React has committed the state update. Callers in JSX pass nothing.
   */

  const tone: Tone = !searched ? "watch" : results.length > 0 ? "replace" : "healthy"
  const headline = !searched
    ? "Check your model."
    : results.length > 0
    ? `${results.length} recall${results.length !== 1 ? "s" : ""} found.`
    : "No recalls found."
  const subline = !searched
    ? "Enter your refrigerator brand or model number. We'll cross-reference the CPSC recall database (saferproducts.gov)."
    : results.length > 0
    ? `Matching CPSC recalls for "${searchTerm}". Click through for the official remedy from the manufacturer — usually a free repair, replacement, or refund.`
    : `No CPSC recall in our database matches "${searchTerm}". For the most current information, also check cpsc.gov/Recalls.`

  return (
    <ToolPageShell
      toolName="Refrigerator Recall Checker"
      heroEyebrow="Specialty"
      heroTitlePre="Has your refrigerator"
      heroTitleItalic="been recalled?"
      heroSubtitle="Search the U.S. Consumer Product Safety Commission (CPSC) recall database by brand or model number. We surface every matching recall — with the official hazard, remedy, and CPSC link."
      heroMeta={["⏱ ~30 sec", "CPSC · saferproducts.gov"]}
      heroShare={<ToolShareBar title="Has your refrigerator been recalled?" />}
      faqs={FAQS}
      relatedTools={RELATED}
      cta={{
        title: "Recalled or replacing? Find a safer model.",
        body: "Browse top-freezer refrigerators with current safety records, modern energy efficiency, and live retailer pricing.",
        buttonLabel: "Browse top-freezer refrigerators",
        buttonHref: "/collections/top-freezer-refrigerators",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Search the CPSC database
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900 mb-2">
                  Brand or model number
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="e.g. Samsung, Frigidaire RF28R7351SR"
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
                <span className="block text-[10px] text-gray-500 mt-1">
                  Source: U.S. CPSC recall database (saferproducts.gov)
                </span>
              </div>

              <button
                type="button"
                onClick={() => handleSearch()}
                disabled={!searchTerm.trim() || loading}
                className="w-full h-12 rounded-lg bg-gray-900 text-white text-[14px] font-extrabold tracking-tight inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                {loading ? "Checking…" : "Check recall database"}
              </button>
            </div>
          </div>

          {/* Verdict */}
          <div>
            <div
              className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
              style={{
                background: TONE_BG[tone],
                border: `1px solid ${TONE_BORDER[tone]}30`,
              }}
            >
              <div
                className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                style={{ color: TONE_BORDER[tone] }}
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ background: TONE_BORDER[tone] }}
                />
                {TONE_LABEL[tone]}
              </div>
              <div
                className="text-gray-900 font-black"
                style={{
                  fontSize: "clamp(40px, 6vw, 72px)",
                  letterSpacing: "-0.05em",
                  lineHeight: 0.95,
                  textWrap: "balance" as never,
                }}
              >
                {headline}
              </div>
              <p
                className="mt-5 md:mt-6 text-gray-700"
                style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
              >
                {subline}
              </p>
            </div>

            {/* Recall details */}
            {searched && results.length > 0 && (
              <div className="space-y-4">
                {results.map((r) => (
                  <div key={r.recall_id} className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-extrabold text-gray-900 text-[16px] leading-[1.35]">{r.title}</h3>
                      <span className="text-[11px] text-gray-500 whitespace-nowrap font-mono">
                        {new Date(r.recall_date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>

                    <p className="text-[13px] text-gray-700 leading-[1.55] mb-4">{r.description}</p>

                    {Array.isArray(r.hazards) && r.hazards.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-3 mb-3 border border-red-200">
                        <span className="text-[10px] font-bold text-red-800 uppercase tracking-[0.12em]">
                          Hazard
                        </span>
                        {r.hazards.map((h: any, i: number) => (
                          <p key={i} className="text-[13px] text-red-700 mt-1">
                            {h.Name}
                          </p>
                        ))}
                      </div>
                    )}

                    {Array.isArray(r.remedies) && r.remedies.length > 0 && (
                      <div className="bg-blue-50 rounded-lg p-3 mb-3 border border-blue-200">
                        <span className="text-[10px] font-bold text-blue-800 uppercase tracking-[0.12em]">
                          Remedy
                        </span>
                        {r.remedies.map((rem: any, i: number) => (
                          <p key={i} className="text-[13px] text-blue-700 mt-1">
                            {rem.Name}
                          </p>
                        ))}
                      </div>
                    )}

                    {Array.isArray(r.images) && r.images.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {r.images.slice(0, 2).map((img: any, i: number) => (
                          <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border">
                            <Image src={img.URL} alt={(img.Caption || r.title).endsWith(" | Fridge.com") ? (img.Caption || r.title) : `${img.Caption || r.title} | Fridge.com`} fill className="object-cover" />
                          </div>
                        ))}
                      </div>
                    )}

                    {r.consumer_contact && (
                      <p className="text-[12px] text-gray-600 mb-3">{r.consumer_contact}</p>
                    )}

                    {r.url && (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[13px] font-bold text-[#0469f0] hover:underline"
                      >
                        View full recall on CPSC.gov <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* No-results helper card */}
            {searched && results.length === 0 && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                  style={{ background: TONE_BORDER.healthy }}
                >
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-extrabold text-[14px]">No CPSC matches in our mirror</div>
                  <p className="text-[13px] text-gray-600 leading-[1.5] mt-0.5">
                    For the latest recalls — especially those issued in the past 24–48 hours — also check{" "}
                    <a
                      href="https://www.cpsc.gov/Recalls"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      cpsc.gov/Recalls
                    </a>{" "}
                    directly.
                  </p>
                </div>
              </div>
            )}

            {/* Pre-search nudge */}
            {!searched && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                  style={{ background: "#ff9951" }}
                >
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <p className="text-[13px] text-gray-700 leading-[1.55]">
                  Tip: brand name first (e.g., Samsung, Frigidaire, and more). Then try the model number from the rating
                  plate — usually inside the fridge on the side wall or behind the crisper drawer.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If you need to replace"
          title="Top-freezer refrigerators with clean safety records"
          description="The simplest, most reliable layout — and the strongest value tier in the category."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "CPSC database",
                d: "We mirror the U.S. Consumer Product Safety Commission recall feed from saferproducts.gov — the federal source of truth for refrigerator and freezer recalls.",
              },
              {
                n: "02",
                t: "Title + description match",
                d: "Your search term is matched (case-insensitive, partial) against the recall title and description. Brand and model number both work.",
              },
              {
                n: "03",
                t: "Verdict + remedy",
                d: "Any match → replace tone with the official hazard and CPSC remedy link. No match → healthy tone with a fallback to cpsc.gov for absolute-latest recalls.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">{m.t}</h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </ToolPageShell>
  )
}
